/**
 * Client-side validation matching the Smithy model constraints from
 * LangHubServiceModel/model/string/operations.smithy (DescribeStringRequest).
 */

import type { TranslateFn } from 'src/i18n/shim/useTranslation';

const ID_PATTERN = /^(?=.*[a-zA-Z0-9])[a-zA-Z0-9_-]+$/;
const CX_ID_PATTERN = /^[a-z0-9.-]+$/;

/**
 * Product cap shared by the namespace name, string group name, and string ID.
 * Deliberately stricter than the backend Smithy `@length` maximums for these
 * identifiers (200 / 175 / 200) so all three are constrained to 50 characters
 * both here and in the create/edit forms.
 */
const IDENTIFIER_MAX_LENGTH = 50;

/** Named rather than inlined so the figure in the error message cannot drift from the check. */
const CX_ID_MAX_LENGTH = 256;

export interface ValidationError {
  field: string;
  message: string;
}

export function validateStringDetailParams(
  t: TranslateFn,
  params: {
    namespaceId?: string;
    stringGroupId?: string;
    stringId?: string;
    variant?: string | null;
    version?: string | null;
  },
): ValidationError[] {
  const errors: ValidationError[] = [];

  // namespaceId: required, capped at IDENTIFIER_MAX_LENGTH chars, pattern
  if (!params.namespaceId) {
    errors.push({
      field: 'namespaceId',
      message: t('string-validation-namespace-required'),
    });
  } else if (params.namespaceId.length > IDENTIFIER_MAX_LENGTH) {
    errors.push({
      field: 'namespaceId',
      message: t('string-validation-namespace-length', {
        max: IDENTIFIER_MAX_LENGTH,
      }),
    });
  } else if (!ID_PATTERN.test(params.namespaceId)) {
    errors.push({
      field: 'namespaceId',
      message: t('string-validation-namespace-pattern'),
    });
  }

  // stringGroupId: required, capped at IDENTIFIER_MAX_LENGTH chars, same pattern
  if (!params.stringGroupId) {
    errors.push({
      field: 'stringGroupId',
      message: t('string-validation-group-required'),
    });
  } else if (params.stringGroupId.length > IDENTIFIER_MAX_LENGTH) {
    errors.push({
      field: 'stringGroupId',
      message: t('string-validation-group-length', {
        max: IDENTIFIER_MAX_LENGTH,
      }),
    });
  } else if (!ID_PATTERN.test(params.stringGroupId)) {
    errors.push({
      field: 'stringGroupId',
      message: t('string-validation-group-pattern'),
    });
  }

  // stringId: required, capped at IDENTIFIER_MAX_LENGTH chars, same pattern
  if (!params.stringId) {
    errors.push({
      field: 'stringId',
      message: t('string-validation-string-required'),
    });
  } else if (params.stringId.length > IDENTIFIER_MAX_LENGTH) {
    errors.push({
      field: 'stringId',
      message: t('string-validation-string-length', {
        max: IDENTIFIER_MAX_LENGTH,
      }),
    });
  } else if (!ID_PATTERN.test(params.stringId)) {
    errors.push({
      field: 'stringId',
      message: t('string-validation-string-pattern'),
    });
  }

  // variant (cxId): optional; if present must be 1-256 chars, pattern ^[a-z0-9.-]+$
  if (params.variant !== null && params.variant !== undefined) {
    if (params.variant.length === 0) {
      errors.push({
        field: 'variant',
        message: t('string-validation-variant-cxid-empty'),
      });
    } else if (params.variant.length > CX_ID_MAX_LENGTH) {
      errors.push({
        field: 'variant',
        message: t('string-validation-variant-cxid-length', {
          max: CX_ID_MAX_LENGTH,
        }),
      });
    } else if (!CX_ID_PATTERN.test(params.variant)) {
      errors.push({
        field: 'variant',
        message: t('string-validation-variant-cxid-pattern'),
      });
    }
  }

  // version (stringGroupVersion): optional; if present must be a positive integer >= 1
  // Matches Smithy constraint: @range(min: 1) on stringGroupVersion: Long
  if (params.version !== null && params.version !== undefined) {
    const parsed = Number(params.version);
    if (!Number.isFinite(parsed) || !Number.isInteger(parsed) || parsed < 1) {
      errors.push({
        field: 'version',
        message: t('string-validation-version-positive'),
      });
    }
  }

  return errors;
}
